// "My practice" - the private §8 self-development mirror (therapy_indices.md).
// Averages over the clinician's OWN sessions only; never shown to anyone else.
import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, LinearProgress, Stack, Typography } from '@mui/material';
import lpsService from '../services/lpsService';

const ROWS = [
  ['talkShare', 'Talk share', 1, (v) => `${Math.round(v * 100)}%`,
    'How much of the session you speak. Leaving room is usually the goal.'],
  ['openQuestionRatio', 'Open-question ratio', 1, (v) => `${Math.round(v * 100)}%`,
    'Open questions invite elaboration; closed ones confirm facts.'],
  ['reflectionsToQuestions', 'Reflections : questions', 1.5, (v) => v.toFixed(2),
    'Reflecting beats interrogating for alliance-building.'],
  ['interruptionRate', 'Interruption rate', 0.2, (v) => v.toFixed(2),
    'How often you start talking before the client finishes.'],
  ['silenceTolerance', 'Silence tolerance', 1, (v) => `${Math.round(v * 100)}%`,
    'Comfort leaving pauses unfilled - space to process.'],
  ['ownResponseLatencySeconds', 'Own response latency', 6, (v) => `${v.toFixed(1)}s`,
    'How long you wait before responding.'],
];

export default function MyPracticePage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    lpsService.practiceSummary().then(setData).catch(() => setData({ sessions: 0 }));
  }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 760 }}>
      <Typography variant="h5" fontWeight={700}>My practice</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        A private self-development mirror computed from your own sessions
        {data?.sessions ? ` (last ${data.sessions})` : ''}. Nobody else sees this page —
        it is a supervision aid, never a report card.
      </Typography>

      {data === null && <LinearProgress />}
      {data && data.sessions === 0 && (
        <Typography color="text.secondary" sx={{ p: 4 }} align="center">
          Once your sessions have analytics, your practice mirror appears here.
        </Typography>
      )}
      {data && data.sessions > 0 && (
        <Stack spacing={1.5}>
          {ROWS.filter(([k]) => data[k] != null).map(([k, label, max, fmt, hint]) => (
            <Card variant="outlined" key={k}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ width: 220 }}>
                    <Typography fontWeight={600}>{label}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress variant="determinate"
                      value={Math.min(100, (data[k] / max) * 100)}
                      sx={{ height: 8, borderRadius: 4 }} />
                  </Box>
                  <Typography sx={{ width: 64, textAlign: 'right' }} fontWeight={700}>
                    {fmt(data[k])}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">{hint}</Typography>
              </CardContent>
            </Card>
          ))}
          {(data.elicitationOpenMeanWords != null || data.elicitationClosedMeanWords != null) && (
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography fontWeight={600}>Elicitation effectiveness</Typography>
                <Typography variant="body2" color="text.secondary">
                  Mean client elaboration after your questions:
                  {data.elicitationOpenMeanWords != null && ` open → ${data.elicitationOpenMeanWords} words`}
                  {data.elicitationClosedMeanWords != null && ` · closed → ${data.elicitationClosedMeanWords} words`}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}
    </Box>
  );
}
